import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserTimes, FaSearch, FaEnvelope, FaPhone, FaCalendarAlt, FaIdCard, FaMapMarkerAlt,
  FaRedo, FaWallet, FaChevronLeft, FaChevronRight, FaExclamationTriangle
} from "react-icons/fa";

const UnVerifiedReporter = () => {
  const [reporters, setReporters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const fetchUnverifiedReporters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/super/admin/get/all/unverified/reporters`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const reportersData = response.data.unverifiedReporters || response.data.data || response.data || [];
      if (Array.isArray(reportersData)) {
        setReporters(reportersData);
      } else {
        setReporters([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch unverified reporters");
      setReporters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnverifiedReporters();
  }, []);

  const filteredReporters = reporters.filter((reporter) => {
    const term = searchTerm.toLowerCase();
    return (
      (reporter.name && reporter.name.toLowerCase().includes(term)) ||
      (reporter.email && reporter.email.toLowerCase().includes(term)) ||
      (reporter.mobileNo && reporter.mobileNo.toString().toLowerCase().includes(term)) ||
      (reporter.city && reporter.city.toLowerCase().includes(term)) ||
      (reporter.state && reporter.state.toLowerCase().includes(term))
    );
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReporters.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReporters.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans">
      <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 z-50"></div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-red-500/20 ring-4 ring-red-50">
            <FaUserTimes />
          </div>
          <div>
            <h1 className="text-3xl font-black text-[#1e293b] tracking-tight">
              Unverified Registry
            </h1>
            <p className="text-[#94a3b8] text-xs font-bold tracking-tight mt-1 uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              {reporters.length} accounts awaiting verification
            </p>
          </div>
        </div>
        <button
          onClick={fetchUnverifiedReporters}
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold active:scale-95"
        >
          <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
          Audit Queue
        </button>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-red-500/5 border border-[#f1f5f9] overflow-hidden">
        <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center text-lg">
              <FaSearch />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1e293b]">Verification Queue</h3>
              <p className="text-xs text-[#94a3b8] font-bold">{filteredReporters.length} profiles flagged</p>
            </div>
          </div>
          <div className="relative w-full md:w-96 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-red-500 transition-colors" />
            <input
              type="text"
              placeholder="Search unverified accounts..."
              className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-red-500/10 focus:bg-white focus:border-red-200 transition-all text-sm font-medium text-[#334155]"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Awaiting Verification</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Status</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Wallet</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Contact Access</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Geography</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="5" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                  </tr>
                ))
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-8 py-20 text-center">
                    <p className="text-[#94a3b8] font-bold uppercase">No unverified reporters found</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((reporter, idx) => (
                  <tr key={reporter._id} className="hover:bg-red-50/5 transition-all duration-200 group">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white text-xl font-black shadow-lg shadow-red-500/20`}>
                          {reporter.name ? reporter.name.charAt(0).toUpperCase() : <FaUserTimes />}
                        </div>
                        <div>
                          <p className="text-base font-black text-[#1e293b] uppercase tracking-tight">{reporter.name || "Unknown Agent"}</p>
                          <p className="text-[10px] text-[#94a3b8] font-extrabold uppercase mt-0.5 tracking-wider font-mono">ID: {reporter._id?.slice(-8)}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap text-center">
                      <span className="px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-600 border border-red-100 flex items-center justify-center gap-2 mx-auto w-fit">
                        <FaExclamationTriangle size={10} /> RESTRIDCTED
                      </span>
                      <p className="text-[9px] text-gray-400 font-bold uppercase mt-1 tracking-tighter">Registered {reporter.createdAt ? new Date(reporter.createdAt).toLocaleDateString() : 'N/A'}</p>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap text-center">
                      <div className="inline-flex items-center gap-2 text-sm font-black text-[#1e293b]">
                        <FaWallet className="text-gray-300" />
                        ₹{(reporter.walletBalance || 0).toFixed(2)}
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{reporter.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{reporter.mobile || 'N/A'}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-[#1e293b] uppercase tracking-tighter">{reporter.city || "Rest of India"}</span>
                        <span className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest">{reporter.state || "National"}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#64748b] font-bold uppercase tracking-widest">
              Page {currentPage} / {totalPages}
            </p>
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-[#e2e8f0]">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-red-50 hover:text-red-600 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronLeft size={12} />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:bg-red-50 hover:text-red-600 disabled:opacity-30 transition-all rounded-xl"
              >
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnVerifiedReporter;
